import React, { useState, useEffect } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { injectIntl } from '../../util/reactIntl';
import { IoMdClose, IoMdInformationCircleOutline } from 'react-icons/io';
import Select from '../../components/ReactSelect/ReactSelect';

import config from '../../config';

import filterIcon from '../../assets/filters-icon.svg';

import css from './BrowseFreelancersPage.css';

const BrowseFreelancersFiltersComponent = props => {
  const {
    skillOptions,
    toolOptions,
    intl,
    currentFilters,
    setCurrentFilters,
    onClearAllFilters,
    constructParamsFromCurrentFilters,
    resetFilters,
    setResetFilters,
  } = props;

  // Local state for checkboxes
  const [hourlyRate, setHourlyRate] = useState([]);

  // Mobile-specific state for pending filters (applied only when search button is clicked)
  const [mobileFilters, setMobileFilters] = useState([]);
  const [mobileHourlyRate, setMobileHourlyRate] = useState([]);

  // Initialize checkboxes from currentFilters when component mounts or filters change
  useEffect(() => {
    const hourlyRateValues = currentFilters
      .filter(filter => filter.field === 'hourlyRate')
      .map(filter => filter.value);

    setHourlyRate(hourlyRateValues);

    // Initialize mobile state with current filters
    setMobileFilters([...currentFilters]);
    setMobileHourlyRate([...hourlyRateValues]);
  }, [currentFilters]);

  // Handle reset filters signal from parent
  useEffect(() => {
    if (resetFilters) {
      // Clear all local state
      setHourlyRate([]);
      setMobileFilters([]);
      setMobileHourlyRate([]);

      // Reset the trigger flag
      setTimeout(() => {
        setResetFilters(false);
      }, 100);
    }
  }, [resetFilters, setResetFilters]);

  const skillsLabel = <FormattedMessage id="BrowseFreelancersFilters.skills" />;
  const toolsLabel = <FormattedMessage id="BrowseFreelancersFilters.tools" />;
  const hourlyRateLabel = <FormattedMessage id="BrowseFreelancersFilters.hourlyRate" />;
  const languageLabel = <FormattedMessage id="BrowseFreelancersFilters.language" />;
  const hobbiesAndInterestsLabel = <FormattedMessage id="BrowseFreelancersFilters.hobbies" />;
  const clearFiltersLabel = (
    <FormattedMessage
      id="BrowseFreelancersFilters.clearFilters"
      values={{ count: currentFilters.length }}
    />
  );

  const clearMobileFiltersLabel = (
    <FormattedMessage id="BrowseFreelancersFilters.clearMobileFilters" />
  );
  // Function to remove a filter
  const removeFilter = filterId => {
    const filterToRemove = currentFilters.find(filter => filter.id === filterId);

    // If it's a checkbox filter, also update local state
    if (filterToRemove) {
      if (filterToRemove.field === 'hourlyRate') {
        setHourlyRate(prev => prev.filter(value => value !== filterToRemove.value));
      }
    }

    const updatedFilters = currentFilters.filter(filter => filter.id !== filterId);
    setCurrentFilters(updatedFilters);

    // Trigger API call with updated filters
    setTimeout(() => {
      constructParamsFromCurrentFilters(updatedFilters);
    }, 50);
  };

  const clearFilters = () => {
    // Clear all state
    setCurrentFilters([]);
    setHourlyRate([]);
    setMobileFilters([]);
    setMobileHourlyRate([]);

    // Call the parent function to clear URL params
    if (onClearAllFilters) {
      onClearAllFilters();
    }
  };

  // Mobile-specific handlers
  const handleMobileSelectChange = (selectedOption, fieldName) => {
    if (!selectedOption) return;

    // Check if this filter already exists in mobile filters
    const existingFilter = mobileFilters.find(
      filter => filter.field === fieldName && filter.value.key === selectedOption.key
    );

    if (!existingFilter) {
      const newFilter = {
        id: `${fieldName}-${selectedOption.key}`,
        label: getFilterLabel(fieldName, selectedOption),
        field: fieldName,
        value: selectedOption,
      };

      const updatedMobileFilters = [...mobileFilters, newFilter];
      setMobileFilters(updatedMobileFilters);
    }
  };

  const handleMobileCheckboxChange = (fieldName, value, checked) => {
    let updatedLocalState;
    let updatedMobileFilters = [...mobileFilters];

    if (fieldName === 'hourlyRate') {
      if (checked) {
        updatedLocalState = [...mobileHourlyRate, value];
      } else {
        updatedLocalState = mobileHourlyRate.filter(item => item !== value);
      }
      setMobileHourlyRate(updatedLocalState);

      // Remove all existing filters for this field from mobile filters
      updatedMobileFilters = updatedMobileFilters.filter(filter => filter.field !== 'hourlyRate');

      // Add new filters for all checked values
      updatedLocalState.forEach(val => {
        updatedMobileFilters.push({
          id: `hourlyRate-${val}`,
          label: getFilterLabel('hourlyRate', val),
          field: 'hourlyRate',
          value: val,
        });
      });
    }

    setMobileFilters(updatedMobileFilters);
  };

  const removeMobileFilter = filterId => {
    const filterToRemove = mobileFilters.find(filter => filter.id === filterId);

    // If it's a checkbox filter, also update local mobile state
    if (filterToRemove) {
      if (filterToRemove.field === 'hourlyRate') {
        setMobileHourlyRate(prev => prev.filter(value => value !== filterToRemove.value));
      }
    }

    const updatedMobileFilters = mobileFilters.filter(filter => filter.id !== filterId);
    setMobileFilters(updatedMobileFilters);
  };

  const applyMobileFilters = () => {
    // Apply the mobile filters to the main filters
    setCurrentFilters([...mobileFilters]);
    setHourlyRate([...mobileHourlyRate]);

    // Trigger API call with the new filters
    setTimeout(() => {
      constructParamsFromCurrentFilters(mobileFilters);
    }, 50);

    // Close the mobile filters panel
    if (typeof window !== 'undefined') {
      const mobileContainer = document.querySelector(`.${css.filtersContainerMobile}`);
      if (mobileContainer) {
        mobileContainer.classList.remove(css.filtersContainerMobileVisible);
        window.Intercom('update', { hide_default_launcher: false });
      }
    }
  };

  // Helper function to get filter label by field name and value
  const getFilterLabel = (fieldName, value) => {
    switch (fieldName) {
      case 'skills':
        const skill = skillOptions?.find(option => option.key === value.key);
        if (skill && skill.label) {
          // Check if label is from API (string) or FormattedMessage component
          if (typeof skill.label === 'string') {
            return skill.label;
          } else {
            // For FormattedMessage components, use intl.formatMessage with the id
            return intl.formatMessage({ id: skill.label.props.id });
          }
        }
        return value.key || value;
      case 'tools':
        const tool = toolOptions?.find(option => option.key === value.key);
        if (tool && tool.label) {
          // Check if label is from API (string) or FormattedMessage component
          if (typeof tool.label === 'string') {
            return tool.label;
          } else {
            // For FormattedMessage components, use intl.formatMessage with the id
            return intl.formatMessage({ id: tool.label.props.id });
          }
        }
        return value.key || value;
      case 'language':
        const filters = config.custom.filters;
        const languages = filters.find(filter => filter.id === 'languages').config.options;
        const language = languages.find(lang => lang.key === value.key);
        return language ? intl.formatMessage({ id: language.label.props.id }) : value;
      case 'hobbies':
        const hobbyOptions = config.custom.hobbyOptions;
        const hobby = hobbyOptions?.find(option => option.key === value.key);
        return hobby ? intl.formatMessage({ id: hobby.label.props.id }) : value;
      case 'hourlyRate':
        const hourlyRateLabels = {
          '20-39': '20-39 €',
          '40-59': '40-59 €',
          '60-79': '60-79 €',
          '80-99': '80-99 €',
          '100+': '+ 100 €',
        };
        return hourlyRateLabels[value] || value;
      default:
        return value;
    }
  };

  // Handle select changes
  const handleSelectChange = (selectedOption, fieldName) => {
    if (!selectedOption) return;

    // Check if this filter already exists
    const existingFilter = currentFilters.find(
      filter => filter.field === fieldName && filter.value.key === selectedOption.key
    );

    if (!existingFilter) {
      const newFilter = {
        id: `${fieldName}-${selectedOption.key}`,
        label: getFilterLabel(fieldName, selectedOption),
        field: fieldName,
        value: selectedOption,
      };

      const updatedFilters = [...currentFilters, newFilter];
      setCurrentFilters(updatedFilters);

      // Trigger API call with updated filters
      setTimeout(() => {
        constructParamsFromCurrentFilters(updatedFilters);
      }, 50);
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (fieldName, value, checked) => {
    let updatedLocalState;
    let updatedFilters = [...currentFilters];

    if (fieldName === 'hourlyRate') {
      if (checked) {
        updatedLocalState = [...hourlyRate, value];
      } else {
        updatedLocalState = hourlyRate.filter(item => item !== value);
      }
      setHourlyRate(updatedLocalState);

      // Remove all existing filters for this field
      updatedFilters = updatedFilters.filter(filter => filter.field !== 'hourlyRate');

      // Add new filters for all checked values
      updatedLocalState.forEach(val => {
        updatedFilters.push({
          id: `hourlyRate-${val}`,
          label: getFilterLabel('hourlyRate', val),
          field: 'hourlyRate',
          value: val,
        });
      });
    }

    setCurrentFilters(updatedFilters);

    // Trigger API call with updated filters
    setTimeout(() => {
      constructParamsFromCurrentFilters(updatedFilters);
    }, 50);
  };

  const filters = config.custom.filters;
  const languages = filters.find(filter => filter.id === 'languages').config.options;
  const languagesWithValue = languages.map(lang => ({
    ...lang,
    key: lang.key,
    label: intl.formatMessage({ id: lang.label.props.id }),
  }));

  const hobbyOptions = config.custom.hobbyOptions;
  const hobbiesWithValue = hobbyOptions
    .map(hobby => ({
      ...hobby,
      key: hobby.key,
      label: intl.formatMessage({ id: hobby.label.props.id }),
    }))
    .sort((a, b) => {
      return a.label.localeCompare(b.label, config.locale, { sensitivity: 'base' });
    });

  // Filter out already selected options and sort alphabetically by label
  const filteredSkillOptions = skillOptions
    .filter(
      option =>
        !currentFilters.some(filter => filter.field === 'skills' && filter.value.key === option.key)
    )
    .sort((a, b) => {
      const labelA =
        typeof a.label === 'string' ? a.label : intl.formatMessage({ id: a.label.props.id });
      const labelB =
        typeof b.label === 'string' ? b.label : intl.formatMessage({ id: b.label.props.id });
      return labelA.localeCompare(labelB, config.locale, { sensitivity: 'base' });
    });

  const filteredToolOptions = toolOptions
    .filter(
      option =>
        !currentFilters.some(filter => filter.field === 'tools' && filter.value.key === option.key)
    )
    .sort((a, b) => {
      const labelA =
        typeof a.label === 'string' ? a.label : intl.formatMessage({ id: a.label.props.id });
      const labelB =
        typeof b.label === 'string' ? b.label : intl.formatMessage({ id: b.label.props.id });
      return labelA.localeCompare(labelB, config.locale, { sensitivity: 'base' });
    });
  const filteredLanguageOptions = languagesWithValue.filter(
    option =>
      !currentFilters.some(filter => filter.field === 'language' && filter.value.key === option.key)
  );
  const filteredHobbyOptions = hobbiesWithValue.filter(
    option =>
      !currentFilters.some(filter => filter.field === 'hobbies' && filter.value.key === option.key)
  );

  // Mobile filter options (filter out already selected mobile options and sort alphabetically)
  const filteredMobileSkillOptions = skillOptions
    .filter(
      option =>
        !mobileFilters.some(filter => filter.field === 'skills' && filter.value.key === option.key)
    )
    .sort((a, b) => {
      const labelA =
        typeof a.label === 'string' ? a.label : intl.formatMessage({ id: a.label.props.id });
      const labelB =
        typeof b.label === 'string' ? b.label : intl.formatMessage({ id: b.label.props.id });
      return labelA.localeCompare(labelB, config.locale, { sensitivity: 'base' });
    });

  const filteredMobileToolOptions = toolOptions
    .filter(
      option =>
        !mobileFilters.some(filter => filter.field === 'tools' && filter.value.key === option.key)
    )
    .sort((a, b) => {
      const labelA =
        typeof a.label === 'string' ? a.label : intl.formatMessage({ id: a.label.props.id });
      const labelB =
        typeof b.label === 'string' ? b.label : intl.formatMessage({ id: b.label.props.id });
      return labelA.localeCompare(labelB, config.locale, { sensitivity: 'base' });
    });
  const filteredMobileLanguageOptions = languagesWithValue.filter(
    option =>
      !mobileFilters.some(filter => filter.field === 'language' && filter.value.key === option.key)
  );
  const filteredMobileHobbyOptions = hobbiesWithValue.filter(
    option =>
      !mobileFilters.some(filter => filter.field === 'hobbies' && filter.value.key === option.key)
  );

  return (
    <div className={css.filtersForm}>
      <div
        className={css.openFiltersMobile}
        onClick={() => {
          if (typeof window !== 'undefined') {
            const mobileContainer = document.querySelector(`.${css.filtersContainerMobile}`);
            if (mobileContainer) {
              mobileContainer.classList.toggle(css.filtersContainerMobileVisible);
              window.Intercom('update', { hide_default_launcher: true });
            }
          }
        }}
      >
        <img src={filterIcon} alt="Open filters" className={css.openFiltersMobileIcon} />
        <FormattedMessage
          id="BrowseFreelancersFilters.openFilters"
          values={{ count: currentFilters.length }}
        />
      </div>
      <div className={css.filtersContainerMobile}>
        <div className={css.filtersContainerMobileFilters}>
          <div className={css.filtersContainerMobileHeader}>
            <FormattedMessage id="BrowseFreelancersFilters.mobileFiltersHeader" />
            <div
              className={css.closeMobileFilters}
              onClick={() => {
                if (typeof window !== 'undefined') {
                  const mobileContainer = document.querySelector(`.${css.filtersContainerMobile}`);
                  if (mobileContainer) {
                    mobileContainer.classList.toggle(css.filtersContainerMobileVisible);
                    window.Intercom('update', { hide_default_launcher: false });
                  }
                }
              }}
            >
              <IoMdClose className={css.closeMobileFiltersIcon} />
            </div>
          </div>
          {mobileFilters && mobileFilters.length > 0 && (
            <div className={css.currentFilters}>
              {mobileFilters.map(filter => (
                <div key={filter.id} className={css.currentFilterItem}>
                  <div className={css.currentFilterItemLabel}>{filter.label}</div>
                  <button
                    type="button"
                    onClick={() => removeMobileFilter(filter.id)}
                    className={css.removeFilterButton}
                    aria-label={`Remove ${filter.label} filter`}
                  >
                    <IoMdClose className={css.removeFilterIcon} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={css.filterItem}>
            <div className={css.filterLabel}>{skillsLabel}</div>
            <Select
              className={css.filterSelect}
              classNamePrefix="react-select"
              options={filteredMobileSkillOptions}
              placeholder={skillsLabel}
              value={null}
              onChange={selectedOption => handleMobileSelectChange(selectedOption, 'skills')}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.key}
              menuPlacement="auto"
              menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
              components={{
                IndicatorSeparator: () => null,
              }}
              styles={{
                menuPortal: provided => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
          </div>

          <div className={css.filterItem}>
            <div className={css.filterLabel}>{toolsLabel}</div>
            <Select
              className={css.filterSelect}
              classNamePrefix="react-select"
              options={filteredMobileToolOptions}
              placeholder={toolsLabel}
              value={null}
              onChange={selectedOption => handleMobileSelectChange(selectedOption, 'tools')}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.key}
              menuPlacement="auto"
              menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
              components={{
                IndicatorSeparator: () => null,
              }}
              styles={{
                menuPortal: provided => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
          </div>

          <div className={css.filterItem}>
            <div className={css.filterLabel}>{hourlyRateLabel}</div>
            <label className={css.checkboxLabel}>
              <input
                className={css.checkboxInput}
                type="checkbox"
                checked={mobileHourlyRate.includes('20-39')}
                onChange={e => handleMobileCheckboxChange('hourlyRate', '20-39', e.target.checked)}
                id="hourlyRate20-39-mobile"
              />
              20-39 €
            </label>
            <label className={css.checkboxLabel}>
              <input
                className={css.checkboxInput}
                type="checkbox"
                checked={mobileHourlyRate.includes('40-59')}
                onChange={e => handleMobileCheckboxChange('hourlyRate', '40-59', e.target.checked)}
                id="hourlyRate40-59-mobile"
              />
              40-59 €
            </label>
            <label className={css.checkboxLabel}>
              <input
                className={css.checkboxInput}
                type="checkbox"
                checked={mobileHourlyRate.includes('60-79')}
                onChange={e => handleMobileCheckboxChange('hourlyRate', '60-79', e.target.checked)}
                id="hourlyRate60-79-mobile"
              />
              60-79 €
            </label>
            <label className={css.checkboxLabel}>
              <input
                className={css.checkboxInput}
                type="checkbox"
                checked={mobileHourlyRate.includes('80-99')}
                onChange={e => handleMobileCheckboxChange('hourlyRate', '80-99', e.target.checked)}
                id="hourlyRate80-99-mobile"
              />
              80-99 €
            </label>
            <label className={css.checkboxLabel}>
              <input
                className={css.checkboxInput}
                type="checkbox"
                checked={mobileHourlyRate.includes('100+')}
                onChange={e => handleMobileCheckboxChange('hourlyRate', '100+', e.target.checked)}
                id="hourlyRate100+-mobile"
              />
              + 100 €
            </label>
          </div>

          <div className={css.filterItem}>
            <div className={css.filterLabel}>{languageLabel}</div>
            <Select
              className={css.filterSelect}
              classNamePrefix="react-select"
              options={filteredMobileLanguageOptions}
              placeholder={languageLabel}
              value={null}
              onChange={selectedOption => handleMobileSelectChange(selectedOption, 'language')}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.key}
              menuPlacement="auto"
              menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
              components={{
                IndicatorSeparator: () => null,
              }}
              styles={{
                menuPortal: provided => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
          </div>

          <div className={css.filterItem}>
            <div className={css.filterLabel}>{hobbiesAndInterestsLabel}</div>
            <Select
              className={css.filterSelect}
              classNamePrefix="react-select"
              options={filteredMobileHobbyOptions}
              placeholder={hobbiesAndInterestsLabel}
              value={null}
              onChange={selectedOption => handleMobileSelectChange(selectedOption, 'hobbies')}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.key}
              menuPlacement="auto"
              menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
              components={{
                IndicatorSeparator: () => null,
              }}
              styles={{
                menuPortal: provided => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
          </div>
        </div>
        <div className={css.filtersContainerMobileButtons}>
          <button
            type="button"
            className={css.clearFiltersMobileButton}
            onClick={() => {
              // Clear mobile filters (pending changes)
              setMobileFilters([]);
              setMobileHourlyRate([]);

              // Apply the cleared filters (empty filters) to make a new query
              setCurrentFilters([]);
              setHourlyRate([]);

              // Trigger API call with empty filters
              setTimeout(() => {
                constructParamsFromCurrentFilters([]);
              }, 50);

              // Call the parent function to clear URL params
              if (onClearAllFilters) {
                onClearAllFilters();
              }
            }}
            disabled={mobileFilters.length === 0}
          >
            {clearMobileFiltersLabel}
          </button>
          <button type="button" className={css.mobileSearchButton} onClick={applyMobileFilters}>
            <FormattedMessage id="BrowseFreelancersFilters.searchMobile" />
          </button>
        </div>
      </div>
      <div className={css.filtersContainerDesktop}>
        {currentFilters.length > 0 && (
          <div className={css.clearFilters} onClick={clearFilters}>
            {clearFiltersLabel}
          </div>
        )}
        {currentFilters && currentFilters.length > 0 && (
          <div className={css.currentFilters}>
            {currentFilters.map(filter => (
              <div key={filter.id} className={css.currentFilterItem}>
                <div className={css.currentFilterItemLabel}>{filter.label}</div>
                <button
                  type="button"
                  onClick={() => removeFilter(filter.id)}
                  className={css.removeFilterButton}
                  aria-label={`Remove ${filter.label} filter`}
                >
                  <IoMdClose className={css.removeFilterIcon} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className={css.filterItem}>
          <div className={css.filterLabel}>{skillsLabel}</div>
          <Select
            className={css.filterSelect}
            classNamePrefix="react-select"
            options={filteredSkillOptions}
            placeholder={skillsLabel}
            value={null}
            onChange={selectedOption => handleSelectChange(selectedOption, 'skills')}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.key}
            menuPlacement="auto"
            menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
            components={{
              IndicatorSeparator: () => null,
            }}
            styles={{
              menuPortal: provided => ({
                ...provided,
                zIndex: 9999,
              }),
            }}
          />
        </div>

        <div className={css.filterItem}>
          <div className={css.filterLabel}>{toolsLabel}</div>
          <Select
            className={css.filterSelect}
            classNamePrefix="react-select"
            options={filteredToolOptions}
            placeholder={toolsLabel}
            value={null}
            onChange={selectedOption => handleSelectChange(selectedOption, 'tools')}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.key}
            menuPlacement="auto"
            menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
            components={{
              IndicatorSeparator: () => null,
            }}
            styles={{
              menuPortal: provided => ({
                ...provided,
                zIndex: 9999,
              }),
            }}
          />
        </div>

        <div className={css.filterItem}>
          <div className={css.filterLabel}>{hourlyRateLabel}</div>
          {hourlyRate.length > 0 ? (
            <div className={css.hourlyRateDisclaimer}>
              <span className={css.hourlyRateDisclaimerIcon}>
                <IoMdInformationCircleOutline />
              </span>
              <p className={css.hourlyRateDisclaimerText}>
                <FormattedMessage
                  id="BrowseFreelancersFilters.hourlyRateDisclaimer"
                  values={{ b: chunks => <strong>{chunks}</strong> }}
                />
              </p>
            </div>
          ) : null}
          <label className={css.checkboxLabel}>
            <input
              className={css.checkboxInput}
              type="checkbox"
              checked={hourlyRate.includes('20-39')}
              onChange={e => handleCheckboxChange('hourlyRate', '20-39', e.target.checked)}
              id="hourlyRate20-39"
            />
            20-39 €
          </label>
          <label className={css.checkboxLabel}>
            <input
              className={css.checkboxInput}
              type="checkbox"
              checked={hourlyRate.includes('40-59')}
              onChange={e => handleCheckboxChange('hourlyRate', '40-59', e.target.checked)}
              id="hourlyRate40-59"
            />
            40-59 €
          </label>
          <label className={css.checkboxLabel}>
            <input
              className={css.checkboxInput}
              type="checkbox"
              checked={hourlyRate.includes('60-79')}
              onChange={e => handleCheckboxChange('hourlyRate', '60-79', e.target.checked)}
              id="hourlyRate60-79"
            />
            60-79 €
          </label>
          <label className={css.checkboxLabel}>
            <input
              className={css.checkboxInput}
              type="checkbox"
              checked={hourlyRate.includes('80-99')}
              onChange={e => handleCheckboxChange('hourlyRate', '80-99', e.target.checked)}
              id="hourlyRate80-99"
            />
            80-99 €
          </label>
          <label className={css.checkboxLabel}>
            <input
              className={css.checkboxInput}
              type="checkbox"
              checked={hourlyRate.includes('100+')}
              onChange={e => handleCheckboxChange('hourlyRate', '100+', e.target.checked)}
              id="hourlyRate100+"
            />
            + 100 €
          </label>
        </div>

        <div className={css.filterItem}>
          <div className={css.filterLabel}>{languageLabel}</div>
          <Select
            className={css.filterSelect}
            classNamePrefix="react-select"
            options={filteredLanguageOptions}
            placeholder={languageLabel}
            value={null}
            onChange={selectedOption => handleSelectChange(selectedOption, 'language')}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.key}
            menuPlacement="auto"
            menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
            components={{
              IndicatorSeparator: () => null,
            }}
            styles={{
              menuPortal: provided => ({
                ...provided,
                zIndex: 9999,
              }),
            }}
          />
        </div>

        <div className={css.filterItem}>
          <div className={css.filterLabel}>{hobbiesAndInterestsLabel}</div>
          <Select
            className={css.filterSelect}
            classNamePrefix="react-select"
            options={filteredHobbyOptions}
            placeholder={hobbiesAndInterestsLabel}
            value={null}
            onChange={selectedOption => handleSelectChange(selectedOption, 'hobbies')}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.key}
            menuPlacement="auto"
            menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
            components={{
              IndicatorSeparator: () => null,
            }}
            styles={{
              menuPortal: provided => ({
                ...provided,
                zIndex: 9999,
              }),
            }}
          />
        </div>
      </div>
    </div>
  );
};

const BrowseFreelancersFilters = injectIntl(BrowseFreelancersFiltersComponent);

export default BrowseFreelancersFilters;
